import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import pluralize from 'pluralize';

import { useSelectionContext, useSelectionCount } from 'common/contexts/SelectionContext';
import { useCustomSearchParams } from 'common/hooks/customSearchParams';
import { WATCH_LIST_QUERY_PARAMS } from 'pages/TrackAndTrace/constants';
import { useSegment } from 'common/hooks/segment';
import { useSnackbar } from 'common/hooks/snackbar';
import { ArchiveButton } from 'components/ContainerActions';
import { archiveContainers } from '../utils';

interface Props {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

export const Archive: React.FC<Props> = (props) => {
  const { trackEventNoSlack } = useSegment();

  const { searchParams, setSearchParams } = useCustomSearchParams(WATCH_LIST_QUERY_PARAMS);
  const [isArchiving, setIsArchiving] = React.useState<boolean>(false);
  const { selection } = useSelectionContext();
  const displaySnackbar = useSnackbar();
  const queryClient = useQueryClient();

  const count = useSelectionCount(selection);

  const handleOnArchive = async (e: React.MouseEvent<HTMLButtonElement>) => {
    props.onClick?.(e);

    const containerNumbers =
      selection.model === 'page' ? selection.containers.map((c) => c.containerNumber) : [];
    const transactionIds =
      selection.model === 'page' ? selection.containers.map((c) => c.transaction) : [];
    setIsArchiving(true);

    const text = `The selected ${pluralize('container', count)} ${
      count > 1 ? 'has' : 'have'
    } been archived.`;

    trackEventNoSlack('Container Archived', {
      feature: 'trackAndTrace',
      source: 'action_button',
      nbContainers: count,
      containers: containerNumbers,
    });

    try {
      await archiveContainers(
        searchParams.containerNumbers,
        containerNumbers,
        transactionIds,
        searchParams,
        setSearchParams,
        queryClient
      );
      displaySnackbar({
        text,
      });
    } catch {
      displaySnackbar({
        severity: 'error',
        text: 'Error while archiving your containers.',
      });
    } finally {
      setIsArchiving(false);
    }
  };

  return <ArchiveButton onClick={handleOnArchive} loading={isArchiving} />;
};
